"use strict";

import * as Sentry from "@sentry/vue";

const RESOLUTION_FULL_SIZE_APPROX = "full_size_approx";
const RESOLUTION_FULL_SIZE = "full_size";
const RESOLUTION_HALF_SIZE = "half_size";
const RESOLUTION_CUSTOM = "custom";

const RESOLUTIONS_CYCLE = {
  [RESOLUTION_CUSTOM]: RESOLUTION_HALF_SIZE,
  [RESOLUTION_HALF_SIZE]: RESOLUTION_FULL_SIZE,
  [RESOLUTION_FULL_SIZE]: RESOLUTION_FULL_SIZE_APPROX
};

function checkVideoStreamAlive(videoElement) {
  const canvas = document.createElement("canvas");
  const blank = document.createElement("canvas");
  const ctx = blank.getContext("2d");
  canvas.width = videoElement.width;
  canvas.height = videoElement.height;
  canvas.getContext("2d").drawImage(videoElement, 0, 0, canvas.width, canvas.height);
  blank.width = canvas.width;
  blank.height = canvas.height;
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, blank.width, blank.height);
  return blank.toDataURL() !== canvas.toDataURL();
}

/** Combine requestAnimationFrame and a maximum FPS
 *
 */
class FPSController {
  /**
   *
   * @param fps {number} FPS at which to run the controller
   */
  constructor(fps) {
    this.frameInterval = 1000 / fps;
    this.startTime = undefined;
    this.stopped = false;
  }

  /**
   * Execture `f` using requestAnimationFrame at specific frame rate
   * @param f {function} function to run at specific frame rate
   */
  runAtFrameRate(f) {
    const controller = this;
    function f2(newtime, forced) {
      let elapsed = newtime - controller.startTime;
      if (forced || elapsed > controller.frameInterval) {
        controller.startTime = newtime;
        f();
      }
      if (!controller.stopped) {
        let nextFrameDelay = controller.frameInterval - elapsed;
        setTimeout(requestAnimationFrame, nextFrameDelay < 0 ? 0 : nextFrameDelay, f2);
      }
    }
    this.startTime = window.performance.now();
    f2(this.startTime, true);
  }

  stop() {
    this.stopped = true;
  }
}

export class VideoAuthenticator {
  cameraManager;
  #pArray;
  #array;
  dataLogger = null;

  constructor(callback, scanEngineSettings, customPrefixes, usableCameras, mobileDeviceInfo, useWorkers) {
    this.callback = callback;
    this.scanEngineSettings = scanEngineSettings;
    this.customPrefixes = customPrefixes;
    this.usableCameras = usableCameras;
    this.mobileDeviceInfo = mobileDeviceInfo;
    this.useWorkers = useWorkers;

    this.videoElement = document.getElementById("video-element");

    this.doProcessing = false;
    this.running = false;
    this.ready = false;
    this.fps = 30;
    this.cropScale = 1;
    this.lastTime = Date.now();
    this.hiddenCanvas = document.createElement("canvas");
    this.hiddenCanvas.className = "display-canvas";
    this.hiddenContext = this.hiddenCanvas.getContext("2d", { alpha: false, willReadFrequently: true }); // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext which type do we want to choose?
  }
  #applySizeToElements(settings) {
    this.streamWidth = settings.width;
    this.streamHeight = settings.height;

    // Scale to apply to the video element to get a visual zoom of 2 independently of the zoom applied by the camera
    this.cropScale = 2 / (settings.zoom / this.cameraManager.getUnitZoomLevel());
    if (this.cropScale < 1.0) {
      // avoid downscaling the video element (to prevent bands around it)
      this.cropScale = 1.0;
    }
    // TODO: move that outside of authenticator when authenticator has some state change callback
    this.videoElement.style.transform = `scale(${this.cropScale})`;

    this.width = Math.min(Math.floor(settings.width / this.cropScale), this.streamWidth);
    this.height = Math.min(Math.floor(settings.height / this.cropScale), this.streamHeight);

    this.hiddenCanvas.width = this.width;
    this.hiddenCanvas.height = this.height;

    this.cropPositionX = Math.floor((this.streamWidth - this.width) / 2);
    this.cropPositionY = Math.floor((this.streamHeight - this.height) / 2);

    console.log({
      streamSize: { width: this.streamWidth, height: this.streamHeight },
      frameSize: { width: this.width, height: this.height },
      cropScale: this.cropScale,
      hiddenCanvasSize: { width: this.hiddenCanvas.width, height: this.hiddenCanvas.height },
      cropPositionX: { x0: this.cropPositionX, y0: this.cropPositionY }
    });

    // Delaying init event by a bit to make sure the size has been set
    // setTimeout(function () {
    //     sendEvent('INIT')
    // }, 200)
  }

  startScanningLoop() {
    let controller = new FPSController(this.fps);
    let thisLocal = this; // could also use loop.bind(this) to avoid that

    let isProcessorIdle = true;
    let loop = function () {
      //Show populate the preview    TODO: is this the way to go?
      if (isProcessorIdle && !(typeof wasmM === "undefined")) {
        isProcessorIdle = false;
        // In WeChat / browsers without workers, we don't need to check for !wasM.isAnalyseFrameAsyncRunning()
        if (!thisLocal.useWorkers || !wasmM.isAnalyseFrameAsyncRunning()) {
          // write to the canvas
          thisLocal.hiddenContext.drawImage(
            thisLocal.videoElement,
            thisLocal.cropPositionX,
            thisLocal.cropPositionY,
            thisLocal.hiddenCanvas.width,
            thisLocal.hiddenCanvas.height,
            0,
            0,
            thisLocal.hiddenCanvas.width,
            thisLocal.hiddenCanvas.height
          );

          // read from the canvas
          try {
            const canvasData = thisLocal.hiddenContext.getImageData(0, 0, thisLocal.hiddenCanvas.width, thisLocal.hiddenCanvas.height);
            let rgba = canvasData.data;
            thisLocal.#array.set(rgba);
            let frameId = thisLocal.dataLogger.getInputFrameId();
            // No async for WeChat
            let res = (thisLocal.useWorkers ? wasmM.analyseFrameArrayAsync : wasmM.analyseFrameArray)(
              frameId,
              thisLocal.#pArray,
              thisLocal.width,
              thisLocal.height,
              4, // nChannels
              1, // selectedChannel: RGBA channel 1 => green
              4 * thisLocal.width // rowSize (uint8): 4 channels times width
            );
            let sdkSendTime = performance.now();
            thisLocal.dataLogger.inputFramesSendTimes.push(sdkSendTime);
            // Do we need to check for res being true here?
            if (thisLocal.dataLogger != null && thisLocal.dataLogger.willUploadFrames) {
              thisLocal.dataLogger.pushToFramesQueue(rgba, thisLocal.hiddenCanvas.width, thisLocal.hiddenCanvas.height);
            }
            thisLocal.dataLogger.incrementInputFrameId();
          } catch (error) {
            console.log(error);
          }
        }
        isProcessorIdle = true;
      }

      if (!thisLocal.doProcessing) {
        controller.stop();
      }
    };
    controller.runAtFrameRate(loop);
  }

  #registerCustomPrefix(prefixURL, prefixParams) {
    let args = [];
    args.push(prefixURL);
    args.push(prefixParams["api"]);

    let codeSpace = prefixParams["code_space"];
    if (codeSpace !== undefined) {
      args.push(codeSpace);
    }
    let regex = prefixParams["regex"];
    if (regex !== undefined) {
      let pattern = regex["pattern"];

      if (pattern !== undefined) {
        args.push(pattern);
        let idGroup = regex["id_group"];
        let customKeyGroup = regex["custom_key_group"];
        if (idGroup !== undefined && customKeyGroup !== undefined) {
          args.push(idGroup);
          args.push(customKeyGroup);
        }
      }
    }
    let prefixEntry = new wasmM.PrefixEntry(...args);
    let ret = wasmM.registerCustomPrefix(prefixEntry);
    prefixEntry.delete();
    return ret;
  }

  #registerCustomPrefixes() {
    for (const [key, value] of Object.entries(this.customPrefixes)) {
      console.log("Registering prefix: " + key + " => " + value);
      this.#registerCustomPrefix(key, value);
    }
  }

  setDataLogger(logger) {
    if (this.dataLogger == null) {
      this.dataLogger = logger;
    }
  }

  setup(cameraId) {
    let isSuccess = true;
    this.cameraManager = new CameraManager(this.videoElement, cameraId, this.scanEngineSettings.se_zoom_factor, this.scanEngineSettings.camera_stream_size, this.usableCameras);

    return this.cameraManager
      .startVideo(this.mobileDeviceInfo)
      .then(() => {
        if (!checkVideoStreamAlive(this.videoElement)) {
          this.cameraManager.stopCamera();
          const nextResolution = this.cameraManager.cycleDownResolution();
          console.log(`Unable to get video stream trying lower resolution "${nextResolution}".`);
          return this.cameraManager.startVideo(this.mobileDeviceInfo);
        } else {
          return;
        }
      })
      .then(() => wasmReady)
      .then(() => {
        const videoTrackSettings = this.cameraManager.getVideoTrackSettings();
        this.#applySizeToElements(videoTrackSettings);

        let numBytes = this.hiddenCanvas.width * this.hiddenCanvas.height * 4;
        this.#pArray = wasmM._malloc(numBytes);
        this.#array = wasmM.HEAPU8.subarray(this.#pArray, this.#pArray + numBytes);

        console.log("Scan engine initialization with settings: ", JSON.stringify(this.scanEngineSettings, null, 2));
        console.log(`Hidden canvas size: [w: ${this.width}, h: ${this.height}]`);
        console.log(`Hidden canvas offset: [x: ${this.cropPositionX}, y: ${this.cropPositionY}]`);

        // TODO: save settings used for instantiasion for later
        const se_applied_zoom_factor = videoTrackSettings.zoom / this.cameraManager.getUnitZoomLevel();
        console.log("Zoom factor applied for Scan Engine: " + se_applied_zoom_factor);
        let scan_engine_initialized = wasmM.initScanEngine(
          this.width,
          this.height,
          this.scanEngineSettings.se_qr_blur,
          this.scanEngineSettings.se_marker_blur,
          se_applied_zoom_factor, // initial zoom factor
          se_applied_zoom_factor, // zoom factor
          se_applied_zoom_factor, // upscale zoom factor limit
          this.scanEngineSettings.se_min_resolution,
          this.scanEngineSettings.se_max_resolution
        );

        if (scan_engine_initialized) {
          this.#registerCustomPrefixes();
        }
        return scan_engine_initialized;
      })
      .then(() => {
        const endDate = new Date();
        console.log("camera ready: ", endDate.getTime());
        // Start scanning loop manually when user phone model is configured
        Promise.resolve(this.callback({ res: "setup done" }))
          // .then(this.startScanningLoop.bind(this))
          .catch((err) => {
            console.log(err.toString());
          });
      })
      .catch((error) => {
        if (this.cameraManager) {
          console.error("Unable to ??", this.cameraManager.getVideoTrackSettings());
          this.cameraManager.stopCamera();
        }
        isSuccess = false;
        return { didSucceed: isSuccess, error };
      })
      .then((res) => {
        if (res && res.error) {
          return {
            didSucceed: isSuccess,
            error: res.error
          };
        } else {
          return {
            didSucceed: isSuccess
          };
        }
      });
  }

  stopScanning() {
    if (this.doProcessing) {
      this.doProcessing = false;
    }
    this.cameraManager.stopCamera();
  }

  startProcessing() {
    if (!this.doProcessing) {
      if (this.dataLogger != null) {
        this.dataLogger.incrementAttemptId();
      }
      let videoTrackSettings = this.cameraManager.getVideoTrackSettings();
      const se_applied_zoom_factor = videoTrackSettings.zoom / this.cameraManager.getUnitZoomLevel();

      let newScanEngineInstantiated = wasmM.initScanEngine(
        this.width,
        this.height,
        this.scanEngineSettings.se_qr_blur,
        this.scanEngineSettings.se_marker_blur,
        se_applied_zoom_factor, // initial zoom factor
        se_applied_zoom_factor, // zoom factor
        se_applied_zoom_factor, // upscale zoom factor limit
        this.scanEngineSettings.se_min_resolution,
        this.scanEngineSettings.se_max_resolution
      );
      if (newScanEngineInstantiated) {
        console.log("a new scan engine has been instantiated");
      }
      this.doProcessing = true;
      this.startScanningLoop();
    }
  }

  stopProcessing() {
    this.doProcessing = false;
  }

  /** If possible returns a ROI (region of interest) in pixels with respect to `overlayContainerObject`
   * else returns `undefined`
   *
   * @param {{width: number, height: number}} targetSize object {width, height} representing the targeted size return by the SDK, used to compute ROI size.
   * @param {boolean} isInside true if the SG is located inside the carrier.
   * @param {HTMLVideoElement} videoObject the video element displaying the video preview, used to compute ROI size.
   * @param {HTMLElement} overlayContainerObject container containing the overlay, used to compute ROI offset.
   * @returns {{y0: number, x0: number, width: number, height: number}|undefined}
   */
  processCodeROI(targetSize, isInside, videoObject, overlayContainerObject) {
    if (!this) {
      return undefined;
    }

    // For object-fit: cover, we need to calculate how the video is scaled and positioned
    const videoRatio = videoObject.videoWidth / videoObject.videoHeight;
    const containerRatio = videoObject.width / videoObject.height;

    let previewScale;
    let offsetX = 0;
    let offsetY = 0;

    if (videoRatio > containerRatio) {
      // Video is wider than container - height matches, width is cropped
      previewScale = videoObject.height / videoObject.videoHeight;
      // Calculate how much of the width is cropped on each side
      offsetX = (videoObject.videoWidth * previewScale - videoObject.width) / 2;
    } else {
      // Video is taller than container - width matches, height is cropped
      previewScale = videoObject.width / videoObject.videoWidth;
      // Calculate how much of the height is cropped on each side
      offsetY = (videoObject.videoHeight * previewScale - videoObject.height) / 2;
    }

    const transformationMatrix = new DOMMatrixReadOnly(window.getComputedStyle(videoObject).getPropertyValue("transform"));

    // transformation.a := scale on X axis
    // transformation.d := scale on Y axis
    const overlayWidth = Math.floor(targetSize.width * previewScale * transformationMatrix.a);
    const overlayHeight = Math.floor(targetSize.height * previewScale * transformationMatrix.d);

    // Adjust x0 and y0 to account for the cropped portions of the video
    const x0 = Math.floor((overlayContainerObject.clientWidth - overlayWidth) / 2) - offsetX * transformationMatrix.a;
    const y0 = Math.floor((overlayContainerObject.clientHeight - overlayHeight) / 2) - offsetY * transformationMatrix.d;

    return { x0, y0, width: overlayWidth, height: overlayHeight };
  }

  getSelectedCameraId() {
    return this.cameraManager ? this.cameraManager.getSelectedCameraId() : undefined;
  }

  getCamera() {
    return this.cameraManager;
  }
}

function getOrientationIndependentSize(fullSize) {
  if (screen.orientation.type.startsWith("portrait")) {
    return { width: fullSize.height, height: fullSize.width };
  }
  return fullSize;
}

class CameraManager {
  videoElement;
  videoTrack;
  videoTracks;
  settings;

  selectedDeviceId;
  baseZoomLevel;
  zoomCapability;
  fullSize;
  streamSize;
  resolution = RESOLUTION_HALF_SIZE;
  maxStreamSize;
  capabilities;

  constructor(videoElement, deviceId, desiredZoomLevel, desiredStreamSize, usableCameras) {
    this.videoElement = videoElement;
    this.selectedDeviceId = deviceId;
    this.baseZoomLevel = desiredZoomLevel ? desiredZoomLevel : 1;
    this.usableCameras = usableCameras;

    if (desiredStreamSize) {
      if (!("width" in desiredStreamSize && "height" in desiredStreamSize)) {
        console.error("desiredStreamSize must contains a width and height element!");
        throw TypeError("desiredStreamSize must contains a width and height element!");
      }
      this.streamSize = desiredStreamSize;
    }
  }

  startVideo(mobileDeviceInfo) {
    // get the media stream base on the basic constraints
    // FUTURE ME: Basic here means the constraints that are used at the mediaDevice/stream level (not requiring audio etc)
    // it may need to be an "augmented" version of the desiredConstraints

    // if the desired device is known set up the video stream
    if (this.selectedDeviceId) {
      return this.setupVideoStream(mobileDeviceInfo, this.selectedDeviceId);
    }

    if (this.usableCameras && this.usableCameras.length) {
      let cam = this.usableCameras[0];
      return this.setupVideoStream(mobileDeviceInfo, cam.deviceId, cam.tracks[0].index);
    }

    // else get correct device id then set up the video stream
    return navigator.mediaDevices
      .enumerateDevices()
      .then(async (devices) => {
        const videoDevices = devices.filter((d) => {
          return "videoinput" === d.kind;
        });

        const validDevicesId = [];
        for (let device of videoDevices) {
          const constraints = {
            audio: false,
            video: {
              deviceId: { exact: device.deviceId },
              facingMode: { exact: "environment" }
            }
          };
          // force sync to avoid opening multiple tracks simultaneously
          await navigator.mediaDevices
            .getUserMedia(constraints)
            .then((stream) => {
              const capabilities = stream.getVideoTracks()[0].getCapabilities();
              if ("torch" in capabilities) {
                validDevicesId.push(device.deviceId);
              }
              stream.getTracks().forEach((t) => {
                t.stop();
                // stream.removeTrack(t);
              });
            })
            .catch((e) => {
              console.log("Can get media device: ", e);
            });
        }
        return validDevicesId[0];
      })
      .then((deviceId) => this.setupVideoStream(mobileDeviceInfo, deviceId));
  }

  setupVideoStream(mobileDeviceInfo, deviceId, trackIndex) {
    const isWechat = mobileDeviceInfo.isWechat();

    this.selectedDeviceId = deviceId;

    trackIndex = trackIndex ? trackIndex : 0;

    let bareMinimalConstraints = {
      audio: false,
      video: {
        width: { min: 1920 },
        height: { min: 1080 },
        facingMode: "environment"
      }
    };

    let zoomLevel = this.baseZoomLevel;

    console.log("Device id: ", deviceId);
    bareMinimalConstraints.video.deviceId = deviceId;
    console.log("bare minimal constraints: ", JSON.stringify(bareMinimalConstraints));

    // Turn off torch by default, if users tap to start, torch will be turned on
    const useTorch = false; // true;

    const thisLocal = this;

    return navigator.mediaDevices
      .getUserMedia(bareMinimalConstraints)
      .then(function (stream) {
        console.log(stream.getVideoTracks());

        // MAYBE there will be more than one track per device.... Not sure how it'll work out with logical cameras in Android for example.
        thisLocal.videoTracks = stream.getVideoTracks();
        thisLocal.videoTrack = thisLocal.videoTracks[trackIndex];

        const capabilities = thisLocal.videoTrack.getCapabilities();
        thisLocal.capabilities = { ...capabilities };

        console.log("Camera capabilities: ", JSON.stringify(capabilities, null, 2));

        thisLocal.zoomCapability = capabilities.zoom;
        zoomLevel = zoomLevel * thisLocal.getUnitZoomLevel();

        const fullSize = {
          width: capabilities.width.max,
          height: capabilities.height.max
        };
        thisLocal.fullSize = fullSize;
        console.log("fullSize: " + thisLocal.fullSize.width + "," + thisLocal.fullSize.height);

        thisLocal.maxStreamSize = getOrientationIndependentSize(fullSize);

        const constraints = {
          width: {},
          height: {},
          facingMode: "environment",
          resizeMode: { exact: "none" },
          frameRate: { ideal: 30 }
        };

        if (thisLocal.streamSize) {
          constraints.width.exact = thisLocal.streamSize.width;
          constraints.height.exact = thisLocal.streamSize.height;
          thisLocal.resolution = RESOLUTION_CUSTOM;
        } else {
          if (!!thisLocal.resolution) {
            if (!("zoom" in thisLocal.capabilities)) {
              thisLocal.resolution = RESOLUTION_FULL_SIZE;
            }
            switch (thisLocal.resolution) {
              case RESOLUTION_HALF_SIZE:
                constraints.width = { ideal: fullSize.width / 2 };
                constraints.height = { ideal: fullSize.height / 2 };
                break;

              case RESOLUTION_FULL_SIZE_APPROX:
                constraints.width = { ideal: fullSize.width };
                constraints.height = { ideal: fullSize.height };
                break;

              case RESOLUTION_FULL_SIZE:
              default:
                constraints.width = { exact: fullSize.width };
                constraints.height = { ideal: fullSize.height };
                break;
            }
          }
        }

        console.log("applying first constraints:", JSON.stringify(constraints, null, 2));

        function videoStreamConfigReadyPromiseFunc() {
          return function (resolve, reject) {
            // the goal is to know when the video has started playing
            thisLocal.videoElement.addEventListener("loadedmetadata", () => {
              // once the video stream is set on the video object,
              // resize the video object otherwise it will be way too large.
              thisLocal.videoElement.width = innerWidth;
              thisLocal.videoElement.height = innerHeight;
            });

            const playVideo = () => {
              thisLocal.videoElement
                .play()
                .then((_) => resolve())
                .catch((error) => reject(error));
            };

            if (!isWechat) {
              thisLocal.videoElement.oncanplay = playVideo;
            }

            thisLocal.videoElement.srcObject = stream;

            if (isWechat) {
              playVideo();
            }
          };
        }

        return thisLocal.videoTrack
          .applyConstraints(constraints)
          .then(() => {
            console.log("Constraints: ", JSON.stringify(thisLocal.videoTrack.getConstraints(), null, 2));
            console.log("Settings: ", JSON.stringify(thisLocal.videoTrack.getSettings(), null, 2));

            const constraints2 = {};
            if (capabilities.sharpness) {
              constraints2.sharpness = capabilities.sharpness.min;
              // advanced.push({sharpness: capabilities.sharpness.min});
            }

            if (capabilities.focusMode && "continuous" in capabilities.focusMode) {
              constraints2.focusMode = "continuous";
              // advanced.push({focusMode: "continuous"});
            }

            if (capabilities.torch) {
              constraints2.torch = useTorch;
            }

            if ("zoom" in capabilities) {
              const zoomToApply = thisLocal.calculateZoomFactorToApply(zoomLevel);
              console.log("Applying zoom level: " + zoomToApply);
              constraints2.zoom = zoomToApply;
            }

            thisLocal.settings = thisLocal.videoTrack.getSettings(); // current state of the device's constraints

            // if (zoomLevel === thisLocal.settings.zoom) {
            //     // TODO: not sure this is correct, we might not have the torch setup
            //     return new Promise(videoStreamConfigReadyPromiseFunc);
            // }

            // Try to apply the "advanced" settings
            // We try 2 different ways, with or without using "advanced" option

            const constraints_temps = constraints;
            constraints_temps.advanced = [constraints2];
            console.log("applying second constraints:", JSON.stringify(constraints_temps, null, 2));
            return thisLocal.videoTrack.applyConstraints(constraints_temps).then(() => {
              thisLocal.settings = thisLocal.videoTrack.getSettings(); // current state of the device's settings

              if (zoomLevel === thisLocal.settings.zoom && thisLocal.settings.torch === useTorch) {
                console.log("Settings2: ", JSON.stringify(thisLocal.settings, null, 2));
                console.log("Constraints2: ", JSON.stringify(thisLocal.videoTrack.getConstraints(), null, 2));

                return new Promise(videoStreamConfigReadyPromiseFunc());
              }

              return thisLocal.videoTrack.applyConstraints(constraints2).then(() => {
                thisLocal.settings = thisLocal.videoTrack.getSettings(); // current state of the device's settings

                console.log("Settings3: ", JSON.stringify(thisLocal.settings, null, 2));
                console.log("Constraints3: ", JSON.stringify(thisLocal.videoTrack.getConstraints(), null, 2));

                // baseZoomLevel = thisLocal.settings.zoom;

                return new Promise(videoStreamConfigReadyPromiseFunc());
              });
            });
          })
          .catch((e) => {
            console.error("Error while applying constraints: ", e);
            throw e;
          });
      })
      .catch((e) => {
        console.error("Failed to get media stream:", e);
        if (this.videoTrack) {
          this.settings = this.videoTrack.getSettings();
        } else {
          return Promise.reject(new Error("Failed to get media stream", { cause: e }));
        }
      });
  }

  stopCamera() {
    if (this.videoTracks) {
      this.videoTracks.forEach((track) => track.stop());
    }

    this.videoTracks = null;
    this.videoTrack = null;
    this.videoElement.srcObject = null;
  }

  getVideoTrackSettings() {
    if (!this.settings) {
      return {};
    }
    const videoTrackSettings = JSON.parse(JSON.stringify(this.settings));

    // Consolidate optional setting: zoom
    if (!("zoom" in videoTrackSettings)) {
      videoTrackSettings.zoom = 1;
    }

    // Consolidate optional setting: torch
    if (!videoTrackSettings.torch) {
      videoTrackSettings.torch = false;
    }

    videoTrackSettings.maxStreamSize = this.maxStreamSize;

    return videoTrackSettings;
  }

  calculateZoomFactorToApply(zoomLevel) {
    const fullSize = this.getFullSize();
    const appliedWidth = this.videoTrack.getSettings().width;
    const appliedHeight = this.videoTrack.getSettings().height;

    const r0 = Math.min(fullSize.width, fullSize.height) / Math.min(appliedWidth, appliedHeight);
    const r1 = Math.max(fullSize.width, fullSize.height) / Math.max(appliedWidth, appliedHeight);

    const zoomRatio = Math.min(r0, r1); // setting it based on the actual (fullSize / size applied)

    let zoomToApply = zoomLevel * zoomRatio;
    const minZoomLevel = this.getUnitZoomLevel();
    const maxZoomLevel = this.getMaxZoomLevel();
    const stepFactorZoomLevels = this.getStepFactorZoomLevels();

    // Apply zoom within the capabilities
    zoomToApply = Math.min(Math.max(zoomToApply, minZoomLevel), maxZoomLevel);

    if (stepFactorZoomLevels) {
      zoomToApply = Math.round((zoomToApply - minZoomLevel) / stepFactorZoomLevels) * stepFactorZoomLevels + minZoomLevel;
    }

    return zoomToApply;
  }

  getFullSize() {
    if (this.fullSize === undefined) {
      throw new Error("Full stream size not yet defined.");
    }
    return this.fullSize;
  }

  getStepFactorZoomLevels() {
    if (this.zoomCapability === undefined) {
      throw new Error("Step factor of zoom levels should be defined first.");
    }
    return this.zoomCapability.step;
  }

  getUnitZoomLevel() {
    if (this.zoomCapability === undefined) {
      console.error("Unit zoom level should be defined first.");
      return 1; // return default unit zoom level = 1 if undefined while logging it.
    }
    // We assume that unit zoom level is the minimum zoom level capable.
    return this.zoomCapability.min;
  }

  getMaxZoomLevel() {
    if (this.zoomCapability === undefined) {
      throw new Error("Max zoom level should be defined first.");
    }
    return this.zoomCapability.max;
  }

  getAppliedConstraints() {
    if (this.videoTrack) {
      return this.videoTrack.getConstraints();
    }
    return {};
  }

  getSelectedCameraId() {
    return this.selectedDeviceId;
  }

  getCapabilities() {
    if (!this.videoTrack) {
      return {};
    }

    return this.videoTrack.getCapabilities();
  }

  cycleDownResolution() {
    const nextResolution = RESOLUTIONS_CYCLE[this.resolution];
    this.resolution = nextResolution;
    return nextResolution;
  }

  async setTorch(enabled) {
    try {
      // First attempt: try with advanced constraints
      await this.videoTrack.applyConstraints({
        advanced: [
          {
            torch: enabled
          }
        ]
      });
    } catch (error) {
      console.log("Failed to set torch with advanced constraints, trying direct method");
      try {
        // Second attempt: try without advanced
        await this.videoTrack.applyConstraints({
          torch: enabled
        });
      } catch (secondError) {
        console.error("Failed to set torch:", secondError);
        Sentry.captureException(new Error(`Error setting torch to ${enabled}`, { cause: secondError }));
        return Promise.reject(new Error(`Error setting torch to ${enabled}`, { cause: secondError }));
      }
    }

    // Update settings after successfully applying constraints
    this.settings = this.videoTrack.getSettings();
  }
}

// Autofocus check: depth / IR sensors typically advertise only ['manual'].
function trackHasAutoFocus(track) {
  const focusMode = track?.capabilities?.focusMode;
  return Array.isArray(focusMode) && (focusMode.includes("continuous") || focusMode.includes("single-shot"));
}

// Camera Selection priority:
//   1. torch present
//   2. lowest focusDistance.min (closest macro focus)
//   3. stable tiebreaker (track index for inner sort, label for cross-device)
function compareTracks(a, b) {
  if (a.hasTorch !== b.hasTorch) return Number(b.hasTorch) - Number(a.hasTorch);
  const fa = a.capabilities?.focusDistance?.min ?? Infinity;
  const fb = b.capabilities?.focusDistance?.min ?? Infinity;
  if (fa !== fb) return fa - fb;
  return a.index - b.index;
}

function compareDevices(a, b) {
  const ta = a.tracks[0];
  const tb = b.tracks[0];
  if (ta.hasTorch !== tb.hasTorch) return Number(tb.hasTorch) - Number(ta.hasTorch);
  const fa = ta.capabilities?.focusDistance?.min ?? Infinity;
  const fb = tb.capabilities?.focusDistance?.min ?? Infinity;
  if (fa !== fb) return fa - fb;
  return (a.label || "").localeCompare(b.label || "");
}

/**
 * Return an array of available camera than could be used for authentication.
 * This method basically return all the usable environment facing cameras ordered by priority.
 * Each element of the list is a potential camera {deviceId: deviceId, tracks: sortedTracks}.
 * Where sortedTracks is an array containing usable sorted by priority, and [0] is the track with the highest priority.
 *
 * /!\ Warning: This function takes long to execute. Its result should be cached locally.
 */
export async function getAvailableCamera(isIOS = true, minimumConstraints = undefined) {
  // Bare minimum requirement for a device to be usable
  let result;
  const camera = minimumConstraints
    ? minimumConstraints
    : {
        height: 1080,
        width: 1920
      };

  try {
    if (true) {
      // Edit MB 23.10.2023: Since the same issue happened on a Samsung device, let's force it every time.
      // TODO: if this works, remove any reference to isIOS
      // on iOS we have to force asking the user permission before calling enumerateDevices
      // otherwise enumerateDevices returns only one camera (the front facing one) without the device ids and labels
      // https://stackoverflow.com/questions/65356567/webrtc-mediadevices-enumeratedevices-returns-empty-deviceid
      const temp_stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: undefined, facingMode: "environment" }, audio: false });
      console.log(temp_stream);
      temp_stream.getTracks().forEach((track) => track.stop());
    }

    const enumerateDevices = await navigator.mediaDevices.enumerateDevices();
    const availableDevices = enumerateDevices.filter((d) => "videoinput" === d.kind);
    const usableDevices = [];

    for (let i = 0; i < availableDevices.length; i++) {
      const currentDevice = availableDevices[i];
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: currentDevice.deviceId } } });
        let potentialDevice = {
          deviceId: currentDevice.deviceId,
          label: currentDevice.label ? currentDevice.label : `Camera ${i + 1}`,
          tracks: []
        };

        try {
          stream.getVideoTracks().forEach((track, trackIndex) => {
            const capabilities = track.getCapabilities();
            const settings = track.getSettings();
            if (settings.facingMode === "environment" && capabilities.height.max >= camera.height && capabilities.width.max >= camera.width) {
              potentialDevice.tracks.push({
                deviceId: currentDevice.deviceId,
                capabilities: capabilities,
                settings: settings,
                hasTorch: !!capabilities.torch,
                index: trackIndex
              });
            }
          });
          if (potentialDevice.tracks.length) {
            potentialDevice.tracks.sort(compareTracks);
            usableDevices.push(potentialDevice);
          }
        } catch (error) {
          console.log("Unable to get stream tracks");
          console.log(currentDevice);
          console.error(error, error.name);
        } finally {
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch (error) {
        // TODO: Should we display a pop up if we need that camera
        console.log("Unable to get the video stream from the camera");
        console.log(currentDevice);
        console.error(error, error.name);
        return Promise.reject(new Error("Unable to get the video stream from the camera", { cause: error }));
      }
    }
    // Filter out cameras that lack autofocus (depth/IR sensors). If the filter would empty the
    // list (e.g. iOS Safari does not expose focusMode), keep all entries as a fallback.
    const autofocusCameras = usableDevices.filter((d) => trackHasAutoFocus(d.tracks[0]));
    const candidates = autofocusCameras.length > 0 ? autofocusCameras : usableDevices;

    candidates.sort(compareDevices);
    return candidates;
  } catch (error) {
    console.error(error);
    return Promise.reject(new Error("Unable to enumerate devices", { cause: error }));
  }
}
